import { CONTEXT_MENU_PT } from '@/shared/ui/RowContextMenu/contextMenuConfig';
import { useEffect, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/shared/lib/errors';
import { getContextMenuEvent } from './contextMenuEvent';
import { ContextMenu } from 'primereact/contextmenu';
import { useAdminAccess } from '@/shared/model/adminAccess';
import { filterAvailableActions } from '@/shared/ui/ActionMenu/filterActions';
import type { ActionMenuItem } from '@/shared/ui/ActionMenu/types';
import type { RowContextMenuProps } from './types';
import classes from './RowContextMenu.module.scss';

/** Показывает доступные действия выбранной строки */
export const RowContextMenu = <TRow extends object,>(
    { getItems, children }: RowContextMenuProps<TRow>
) => {
    const menu = useRef<ContextMenu>(null);
    const revision = useRef(0);
    const retryTimer = useRef<ReturnType<typeof setTimeout>>();
    const [items, setItems] = useState<ActionMenuItem[]>([]);
    const snapshot = useAdminAccess((state) => state.snapshot);

    useEffect(() => () => {
        revision.current += 1;
        clearTimeout(retryTimer.current);
    }, []);

    /** Открывает меню выбранной записи */
    const show = (event: SyntheticEvent, row: TRow): void => {
        event.preventDefault();
        event.stopPropagation();

        setItems([{
            label: 'Загрузка действий...',
            icon: 'pi pi-spin pi-spinner',
            disabled: true
        }]);

        const position = getContextMenuEvent(event);
        menu.current?.show(position);
        const request = ++revision.current;

        /** Загружает действия записи */
        const load = async (): Promise<void> => {
            try {
                const next = await getItems(row);

                if (request !== revision.current) return;

                const allowed = filterAvailableActions(next, useAdminAccess.getState().snapshot);

                setItems(allowed.length
                    ? allowed.map((item) => ({
                        ...item,
                        command: item.command
                            ? (commandEvent) => {
                                const current = filterAvailableActions([item], useAdminAccess.getState().snapshot);

                                if (current.length && !item.disabled) {
                                    void Promise.resolve()
                                        .then(() => item.command?.(commandEvent))
                                        .catch((error: unknown) => toast.error(getErrorMessage(error, 'Не удалось выполнить действие')));
                                }
                            }
                            : undefined,
                    }))
                    : [{
                        label: 'Нет доступных действий',
                        icon: 'pi pi-lock',
                        disabled: true
                    }]);
            } catch {
                if (request !== revision.current) return;

                setItems([{
                    label: 'Не удалось загрузить действия. Повторить',
                    icon: 'pi pi-refresh',
                    command: () => {
                        // PrimeReact закрывает меню после команды, поэтому повтор открывается следующим событием
                        retryTimer.current = setTimeout(() => show(position, row), 0);
                    },
                }]);
            }
        };

        void load();
    };

    return <>
        <ContextMenu
            pt={CONTEXT_MENU_PT}
            ref={menu}
            model={filterAvailableActions(items, snapshot)}
            ariaLabel="Действия со строкой"
            className={classes.contextMenu}
            onHide={() => { revision.current += 1; }}
        />
        {children(show)}
    </>;
};
